import { X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

interface MonthPickerProps {
  value: string; // selected preset key or ""
  onChange: (preset: string) => void;
  disabled?: boolean;
}

const PRESETS: Record<string, { key: string; label: string }[]> = {
  cs: [
    { key: "this_month", label: "Tento měsíc" },
    { key: "next_2m", label: "V příštích 2 měsících" },
    { key: "next_3m", label: "V příštích 3 měsících" },
    { key: "next_4m", label: "V příštích 4 měsících" },
    { key: "next_5m", label: "V příštích 5 měsících" },
    { key: "next_6m", label: "V příštích 6 měsících" },
    { key: "next_1y", label: "V příštím roce" },
    { key: "next_2y", label: "V příštích 2 letech" },
    { key: "next_3y", label: "V příštích 3 letech" },
    { key: "next_4y", label: "V příštích 4 letech" },
  ],
  sk: [
    { key: "this_month", label: "Tento mesiac" },
    { key: "next_2m", label: "V najbližších 2 mesiacoch" },
    { key: "next_3m", label: "V najbližších 3 mesiacoch" },
    { key: "next_4m", label: "V najbližších 4 mesiacoch" },
    { key: "next_5m", label: "V najbližších 5 mesiacoch" },
    { key: "next_6m", label: "V najbližších 6 mesiacoch" },
    { key: "next_1y", label: "V budúcom roku" },
    { key: "next_2y", label: "V najbližších 2 rokoch" },
    { key: "next_3y", label: "V najbližších 3 rokoch" },
    { key: "next_4y", label: "V najbližších 4 rokoch" },
  ],
  uk: [
    { key: "this_month", label: "Цього місяця" },
    { key: "next_2m", label: "Протягом 2 місяців" },
    { key: "next_3m", label: "Протягом 3 місяців" },
    { key: "next_4m", label: "Протягом 4 місяців" },
    { key: "next_5m", label: "Протягом 5 місяців" },
    { key: "next_6m", label: "Протягом 6 місяців" },
    { key: "next_1y", label: "Протягом року" },
    { key: "next_2y", label: "Протягом 2 років" },
    { key: "next_3y", label: "Протягом 3 років" },
    { key: "next_4y", label: "Протягом 4 років" },
  ],
  en: [
    { key: "this_month", label: "This month" },
    { key: "next_2m", label: "Within 2 months" },
    { key: "next_3m", label: "Within 3 months" },
    { key: "next_4m", label: "Within 4 months" },
    { key: "next_5m", label: "Within 5 months" },
    { key: "next_6m", label: "Within 6 months" },
    { key: "next_1y", label: "Within next year" },
    { key: "next_2y", label: "Within 2 years" },
    { key: "next_3y", label: "Within 3 years" },
    { key: "next_4y", label: "Within 4 years" },
  ],
  de: [
    { key: "this_month", label: "Diesen Monat" },
    { key: "next_2m", label: "In den nächsten 2 Monaten" },
    { key: "next_3m", label: "In den nächsten 3 Monaten" },
    { key: "next_4m", label: "In den nächsten 4 Monaten" },
    { key: "next_5m", label: "In den nächsten 5 Monaten" },
    { key: "next_6m", label: "In den nächsten 6 Monaten" },
    { key: "next_1y", label: "Im nächsten Jahr" },
    { key: "next_2y", label: "In den nächsten 2 Jahren" },
    { key: "next_3y", label: "In den nächsten 3 Jahren" },
    { key: "next_4y", label: "In den nächsten 4 Jahren" },
  ],
};

export function MonthPicker({ value, onChange, disabled }: MonthPickerProps) {
  const { lang } = useI18n();
  const presets = PRESETS[lang] || PRESETS.cs;
  const selected = presets.find((p) => p.key === value);

  return (
    <div className="flex flex-wrap gap-1.5">
      {presets.map((preset) => (
        <button
          key={preset.key}
          onClick={() => {
            if (disabled) return;
            onChange(value === preset.key ? "" : preset.key);
          }}
          disabled={disabled}
          className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-colors ${
            value === preset.key
              ? "bg-primary text-primary-foreground"
              : "border border-border text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
          }`}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
}

/** Returns the number of months from now that this preset covers */
export function presetToMonths(preset: string): number {
  switch (preset) {
    case "this_month": return 0;
    case "next_2m": return 2;
    case "next_3m": return 3;
    case "next_4m": return 4;
    case "next_5m": return 5;
    case "next_6m": return 6;
    case "next_1y": return 12;
    case "next_2y": return 24;
    case "next_3y": return 36;
    case "next_4y": return 48;
    default: return -1;
  }
}
